import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '../../hooks/useAuth.js';
import { useMenuStore } from '../../stores/menuStore.js';
import { useOrdersStore } from '../../stores/ordersStore.js';
import platformLogo from '../../assets/imgx.png';

import { MobileTopBar } from './MobileTopBar.jsx';
import { MobileBottomNavNew } from './MobileBottomNavNew.jsx';
import { FloatingCartButton } from './FloatingCartButton.jsx';
import { MobileCartDrawer } from './MobileCartDrawer.jsx';
import { MobileHomeTab } from './MobileHomeTab.jsx';
import { MobileMenusTab } from './MobileMenusTab.jsx';
import { MobileOrdersTab } from './MobileOrdersTab.jsx';
import { MobileMoreTab } from './MobileMoreTab.jsx';
import { DashboardPanel } from '../home/DashboardPanel.jsx';

/**
 * Complete mobile layout wrapper.
 * Tabs: Home (default) | Menus | Orders | Dashboard | More
 * Cart is a floating button + drawer, not in the nav bar.
 */
export const MobileLayout = () => {
  const { outletId, outletName, userName } = useAuth();
  const fetchMenus = useMenuStore((s) => s.fetchMenus);
  const fetchActiveOrders = useOrdersStore((s) => s.fetchActiveOrders);

  const [activeTab, setActiveTab] = useState('home');
  const [cartOpen, setCartOpen] = useState(false);

  const handleTabChange = useCallback((tab) => {
    setCartOpen(false); // close cart overlay when switching tabs
    setActiveTab(tab);
  }, []);

  const toggleCart = useCallback(() => {
    setCartOpen((prev) => !prev);
  }, []);

  // Bootstrap data on mount
  useEffect(() => {
    if (outletId) {
      fetchMenus(outletId);
      fetchActiveOrders(outletId);
    }
  }, [outletId, fetchMenus, fetchActiveOrders]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return <MobileHomeTab />;
      case 'menus':
        return <MobileMenusTab />;
      case 'orders':
        return <MobileOrdersTab />;
      case 'dashboard':
        return <DashboardPanel onBack={() => setActiveTab('home')} />;
      case 'more':
        return <MobileMoreTab />;
      default:
        return <MobileHomeTab />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      {/* Top bar: names on left, logo on right */}
      <MobileTopBar
        outletName={outletName}
        userName={userName}
        logoSrc={platformLogo}
      />

      {/* Main content area */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: '72px', display: 'flex', flexDirection: 'column' }}>
        {renderTabContent()}
      </Box>

      {/* Cart overlay — fills screen above nav bar + button when open */}
      <MobileCartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />

      {/* Floating "New Order" / "Close" button */}
      <FloatingCartButton onClick={toggleCart} isOpen={cartOpen} />

      {/* Bottom navigation — no cart tab */}
      <MobileBottomNavNew
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
    </Box>
  );
};
